import { useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid } from '@material-ui/core';
import { Redirect, Route, Switch } from 'react-router';
import {
  Page,
  Flex,
  GrayBackground,
  WhiteSection
} from 'components/base/layout-components';
import TabList from 'components/TabList';
import { SPACES } from 'common/constants/sizes';
import * as TrackingService from 'common/services/tracking.service';
import AppRouter from 'common/AppRouter';
import {
  getContributorsDefaultView,
  setContributorsDefaultView
} from 'common/services/contributors.service';
import Individuals from './views/individuals';
import Teams from './views/teams';
import {
  TRACKING,
  SETTING_NAMES,
  CONTRIBUTORS_TABS
} from 'common/constants/constants';
import withPermissions from 'connected-components/HOC/PermissionsHOC';
import { isSelectedTeamParentSelector } from 'state/selectors/teams';
import { selectedTeamSelector } from 'state/selectors/ui-state';
import { CAN_EDIT_ACCOUNT_TYPES } from '@linearb/linjs-common';
import SyncTeamsCTA from './views/ctas/SyncTeamsCTA';
import useSettingsService from 'components/hooks/useSettingsService';
import useTeams from 'views/root/views/home/views/settings/views/ContributorsAndTeams/Teams/useTeams';
import useRBACPermissions from '../../hooks/useRBACPermissions';
import useDoraFeatureFlags from '../../hooks/useDoraFeatureFlags';
import AllContributorsTeamsEmptyState from 'components/teams/AllContributorsTeamsEmptyState';
import { LBButton } from '@linearb/buzz';

const StyledBackground = styled(GrayBackground)`
  padding: ${SPACES.small};
`;

const Contributors = ({
  contributors,
  createTeam,
  toggleModal,
  toggleSnackBar,
  isVPMode,
  currentTeams,
  isTeamLicense,
  getTeams,
  gitScopeToggle
}) => {
  const history = useHistory();
  const location = useLocation();
  const { isAllControbutorsTeamSelected } = useDoraFeatureFlags();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const { onConfigureTeam } = useTeams();
  const isShowPersonalInformation = getSettingOrDefinitionValue(
    {
      name: SETTING_NAMES.SHOW_PERSONAL_INFORMATION
    },
    true
  );
  const { isPermitted } = useRBACPermissions();
  const selectedTeam = useSelector(selectedTeamSelector);
  const isSelectedTeamParent = useSelector(isSelectedTeamParentSelector);

  const tabs = isShowPersonalInformation
    ? [
        { name: 'Teams', route: CONTRIBUTORS_TABS.TEAMS },
        { name: 'Members', route: CONTRIBUTORS_TABS.INDIVIDUALS }
      ]
    : [];

  const urlParams = new URLSearchParams(window.location.search);
  if (!!selectedTeam) {
    urlParams.set('teams', selectedTeam.id);
    urlParams.set('page', 0);
  }

  useEffect(() => {
    // when changing selected team in top bar.
    // if (!Group && single team || in individuals tab) && show personal info allowed - redirect to Individuals
    // else ((all teams || in teams tab) && !show personal info) - redirect to Teams
    urlParams.delete('contributors');

    if (
      !isSelectedTeamParent &&
      (!isVPMode ||
        getContributorsDefaultView() === CONTRIBUTORS_TABS.INDIVIDUALS) &&
      isShowPersonalInformation
    ) {
      history.replace({
        pathname: `${AppRouter.CONTRIBUTORS.path}/individuals`,
        search: urlParams.toString()
      });
    } else {
      history.replace({
        pathname: `${AppRouter.CONTRIBUTORS.path}/teams`,
        search: urlParams.toString()
      });
    }
  }, [selectedTeam]);

  const onTabClick = ({ name: selectedName }) => {
    const tab = tabs.find(({ name }) => name === selectedName);
    setContributorsDefaultView(selectedName.toLowerCase());
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: TRACKING.PAGES.PEOPLE,
      page_tab: selectedName
    });
    history.push({
      pathname: `${AppRouter.CONTRIBUTORS.path}/${tab.route}`,
      search: location.search
    });
  };

  const tab = tabs.find(({ route }) => location.pathname.includes(route)) || {};

  const getVPModeTabs = ({ includeAddTeam = false }) => {
    const shouldShowAddTeamButton =
      currentTeams.length &&
      !isTeamLicense &&
      includeAddTeam &&
      (isVPMode || !isShowPersonalInformation) &&
      isPermitted;

    return (
      (isVPMode || isSelectedTeamParent || shouldShowAddTeamButton) && (
        <Grid item xs={12} data-cy='contributors-teams-tabs'>
          <TabList tabs={tabs} onTabClick={onTabClick} selectedTab={tab.name}>
            {shouldShowAddTeamButton ? (
              <Flex items='end' pb='small'>
                <LBButton
                  size='lg'
                  onClick={() => {
                    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                      page: TRACKING.PAGES.PEOPLE,
                      click_type: TRACKING.CLICK.ADD_TEAM
                    });
                    onConfigureTeam();
                  }}
                >
                  Add Team
                </LBButton>
              </Flex>
            ) : null}
          </TabList>
        </Grid>
      )
    );
  };

  return (
    <Page>
      <StyledBackground>
        <WhiteSection style={{ height: isVPMode ? '' : '100%' }}>
          {withPermissions({
            WrappedComponent: getVPModeTabs({ includeAddTeam: true }),
            requiredPermissions: CAN_EDIT_ACCOUNT_TYPES,
            DefaultOnHideComponent: getVPModeTabs({ includeAddTeam: false })
          })}
          <Grid item xs={12} style={{ height: '100%' }}>
            <SyncTeamsCTA />
            {isAllControbutorsTeamSelected ? (
              <AllContributorsTeamsEmptyState />
            ) : (
              <Switch>
                {isShowPersonalInformation && (
                  <Route
                    path={AppRouter.INDIVIDUALS.path}
                    render={() => (
                      <Individuals gitScopeToggle={gitScopeToggle} />
                    )}
                  />
                )}
                <Route
                  path={AppRouter.TEAMS.path}
                  render={() => <Teams gitScopeToggle={gitScopeToggle} />}
                />
                <Route
                  path={AppRouter.CONTRIBUTORS.path}
                  exact
                  render={() => (
                    <Redirect
                      gitScopeToggle={gitScopeToggle}
                      to={{
                        pathname:
                          (isShowPersonalInformation &&
                            getContributorsDefaultView() ===
                              CONTRIBUTORS_TABS.INDIVIDUALS) ||
                          isTeamLicense
                            ? AppRouter.INDIVIDUALS.path
                            : AppRouter.TEAMS.path,
                        search: urlParams.toString()
                      }}
                    />
                  )}
                />
              </Switch>
            )}
          </Grid>
        </WhiteSection>
      </StyledBackground>
    </Page>
  );
};

export default Contributors;
